import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Card,
	Row,
	Col,
	Select,
	DatePicker,
	Table,
	Typography,
	Spin,
	Alert,
	Space,
	Button,
	Empty,
	Form,
	Statistic,
	Progress,
	Tag,
	Tooltip,
} from 'antd';
import { Line, Column } from '@ant-design/plots';
import {
	RedoOutlined,
	ClearOutlined,
	QuestionCircleOutlined,
	ShareAltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { MODAL_IDS } from 'src/enums/EModal';
import { openModal } from '@/store/modal/slice';
import ShareCallLogModal from '../ReceptionistDashboard/LogsComponent/CallLogs/Modals/ShareCallLogModal';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface CallInsightsDashboardProps {
	onViewDetail: (callSid: string) => void;
}

const DEFAULT_PAGE_LIMIT = 10;
const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

const ALL_ANALYSIS_METRICS = [
	'intent_recognition_score',
	'transcription_accuracy_impact_score',
	'clarification_handling_score',
	'human_clarification_disruption_score',
	'task_completion_score',
	'tone_naturalness_score',
	'human_interruption_impact_score',
	'ai_interruption_impact_score',
	'ai_repetition_impact_score',
	'fallback_handling_score',
	'callScoreValue',
	'ai_interruptions',
	'human_interruptions',
	'ai_repetitions',
	'clarification_count',
	'transcription_error_count',
];

const EVALUATION_FIELDS = ALL_ANALYSIS_METRICS.filter((m) => m.includes('_score'));

const RAW_COUNT_METRICS = [
	'ai_interruptions',
	'human_interruptions',
	'ai_repetitions',
	'clarification_count',
	'transcription_error_count',
];

const formatMetricName = (name: string) => {
	return name
		.replace(/_score|_impact|_count/g, '')
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (l) => l.toUpperCase());
};

const METRIC_DESCRIPTIONS = {
	intent_recognition_score:
		"How accurately did the AI understand the caller's intent throughout the call? (10 = perfectly understood, 0 = completely misunderstood)",
	transcription_accuracy_impact_score:
		'How much did likely transcription errors affect the clarity, flow, or correctness of the conversation? (10 = no impact, 0 = complete breakdown)',
	clarification_handling_score:
		'How well did the AI update its understanding based on caller corrections or clarifications?',
	human_clarification_disruption_score:
		'How much did caller clarifications affect the naturalness or flow of the call?',
	task_completion_score:
		'Did the AI successfully guide the caller to a resolution based on their intent?',
	tone_naturalness_score:
		'How natural, friendly, and humanlike did the AI sound across the interaction?',
	human_interruption_impact_score:
		'How much did human interruptions reduce the clarity or flow of the conversation?',
	ai_interruption_impact_score: "How much did the AI's interruptions harm the call flow?",
	ai_repetition_impact_score: 'How much did AI repetition affect the experience?',
	fallback_handling_score:
		"When the AI didn't understand or encountered difficulty, how well did it escalate, offer to text, or fallback to a human?",
};

const transformToBinnedData = (data: any[], xField: string, yField: string) => {
	if (!data || data.length === 0) {
		return [];
	}

	const binned = new Map<string, { ySum: number; count: number }>();
	const isScore = xField.includes('_score') || xField.includes('_impact');

	data.forEach((item) => {
		const xValue = item[xField];
		const yValue = item[yField];

		if (xValue === undefined || yValue === undefined) {
			return;
		}

		let binKey: string;
		if (isScore) {
			binKey = String(Math.floor(xValue));
		} else {
			if (xValue === 0) {
				binKey = '0';
			} else if (xValue === 1) {
				binKey = '1';
			} else if (xValue === 2) {
				binKey = '2';
			} else if (xValue >= 3 && xValue <= 5) {
				binKey = '3-5';
			} else {
				binKey = '6+';
			}
		}

		const existing = binned.get(binKey) || { ySum: 0, count: 0 };
		existing.ySum += yValue;
		existing.count++;
		binned.set(binKey, existing);
	});

	const result = Array.from(binned.entries()).map(([key, value]) => ({
		bin: key,
		avgValue: value.ySum / value.count,
		callVolume: value.count,
	}));

	return result.sort((a, b) => {
		const aNum = parseInt(a.bin, 10);
		const bNum = parseInt(b.bin, 10);
		return aNum - bNum;
	});
};

const CallInsightsDashboard: React.FC<CallInsightsDashboardProps> = ({ onViewDetail }) => {
	const [form] = Form.useForm();
	const dispatch = useAppDispatch();
	const [summaryData, setSummaryData] = useState<any>(null);
	const [tableData, setTableData] = useState<any[]>([]);

	const [trendMetric, setTrendMetric] = useState<string>(EVALUATION_FIELDS[0]);
	const [impactXMetric, setImpactXMetric] = useState<string>('ai_repetitions');
	const [impactYMetric] = useState<string>('callScoreValue');

	const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
	const [loadingTable, setLoadingTable] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(0);

	const fetchSummaryData = useCallback(async (filters: any) => {
		setLoadingSummary(true);
		setError(null);
		try {
			const payload: any = {
				startDate: (filters.dateRange[0] as dayjs.Dayjs).startOf('day').valueOf(),
				endDate: (filters.dateRange[1] as dayjs.Dayjs).endOf('day').valueOf(),
			};
			if (filters.callScore) {
				payload.callScore = filters.callScore;
			}
			const token = LocalStorageUtility.getLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
			const response = await axios.post(`${baseUrl}/api/admin/call-logs/summary`, payload, {
				headers: { Authorization: `Bearer ${token}` },
			});
			setSummaryData(response.data.data);
		} catch (err) {
			const errorMessage = (err as any).response?.data?.message || 'Failed to load summary data.';
			setError(errorMessage);
			setSummaryData(null);
		} finally {
			setLoadingSummary(false);
		}
	}, []);

	const fetchTableData = useCallback(async (filters: any, page: number = 1) => {
		setLoadingTable(true);
		const params: any = {
			limit: DEFAULT_PAGE_LIMIT,
			page,
			startDate: (filters.dateRange[0] as dayjs.Dayjs).startOf('day').valueOf(),
			endDate: (filters.dateRange[1] as dayjs.Dayjs).endOf('day').valueOf(),
		};
		if (filters.callScore) {
			params.callScore = filters.callScore;
		}

		try {
			const token = LocalStorageUtility.getLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
			const response = await axios.get(`${baseUrl}/api/admin/call-logs/table`, {
				params,
				headers: { Authorization: `Bearer ${token}` },
			});
			setTableData(response.data.data.items || []);
			setTotalItems(response.data.data.total || 0);
			setCurrentPage(response.data.data.page || 1);
		} catch (_err) {
			setTableData([]);
			setTotalItems(0);
		} finally {
			setLoadingTable(false);
		}
	}, []);

	useEffect(() => {
		const initialFilters = {
			dateRange: [dayjs().subtract(7, 'days'), dayjs()],
			callScore: null,
		};
		form.setFieldsValue(initialFilters);
		fetchSummaryData(initialFilters);
	}, [form, fetchSummaryData]);

	const handleFilterSubmit = (formValues: any) => {
		fetchSummaryData(formValues);

		if (formValues.callScore) {
			fetchTableData(formValues, 1);
		} else {
			setTableData([]);
			setTotalItems(0);
		}
	};

	const handleResetFilters = () => {
		form.resetFields();
		setSummaryData(null);
		setTableData([]);
	};

	const tableColumns = [
		{ title: 'Call SID', dataIndex: 'callSid', key: 'callSid', width: 280, ellipsis: true },
		{
			title: 'Date',
			dataIndex: 'date',
			key: 'date',
			render: (d: string) => dayjs(d).format('YYYY-MM-DD HH:mm'),
			width: 180,
		},
		{ title: 'From', dataIndex: 'from', key: 'from', width: 150 },
		{ title: 'To', dataIndex: 'to', key: 'to', width: 150 },
		{
			title: 'Score',
			dataIndex: 'callScoreValue',
			key: 'callScoreValue',
			width: 100,
			render: (s: number | string) => {
				if (typeof s === 'number') {
					return <Tag color={s > 3 ? 'green' : 'red'}>{s}/5</Tag>;
				}
				return <Tag>N/A</Tag>;
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 120,
			render: (_: any, record: any) => (
				<Tooltip title="Share Call Log">
					<Button
						icon={<ShareAltOutlined />}
						disabled={!record.shareableLink}
						onClick={(e) => {
							e.stopPropagation(); // Prevent row click
							// Dispatch the action to open the reusable modal
							dispatch(
								openModal({
									modalId: MODAL_IDS.SHARE_CALL_LOG_MODAL,
									data: {
										shareableLink: record.shareableLink,
										debrand: false, // Assuming admin view is always branded
									},
								}),
							);
						}}
					/>
				</Tooltip>
			),
		},
	];

	const lineChartConfig = {
		height: 250,
		xField: 'date',
		yField: 'value',
		seriesField: 'type',
		point: { size: 3 },
		legend: { position: 'top' as const },
	};

	const impactChartData = useMemo(
		() => transformToBinnedData(summaryData?.impactAnalysisData, impactXMetric, impactYMetric),
		[summaryData, impactXMetric, impactYMetric],
	);

	return (
		<Space direction="vertical" size="large" style={{ width: '100%' }}>
			<Title level={2}>Call Insights Dashboard</Title>
			<Card>
				<Form form={form} onFinish={handleFilterSubmit}>
					<Row gutter={[16, 16]} align="bottom">
						<Col xs={24} md={8}>
							<Form.Item
								name="dateRange"
								label="Date Range:"
								rules={[{ required: true, message: 'Please select a date range.' }]}
							>
								<RangePicker style={{ width: '100%' }} />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={4}>
							<Form.Item name="callScore" label="Call Score">
								<Select placeholder="All Scores" allowClear>
									<Select.Option value={5}>5</Select.Option>
									<Select.Option value={4}>4</Select.Option>
									<Select.Option value={3}>3</Select.Option>
									<Select.Option value={2}>2</Select.Option>
									<Select.Option value={1}>1</Select.Option>
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} md={6}>
							<Form.Item label=" " colon={false}>
								<Space>
									<Button icon={<ClearOutlined />} onClick={handleResetFilters}>
										Reset
									</Button>
									<Button
										type="primary"
										htmlType="submit"
										icon={<RedoOutlined />}
										loading={loadingSummary}
									>
										Apply
									</Button>
								</Space>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Card>

			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => setError(null)}
				/>
			)}

			<Spin spinning={loadingSummary}>
				{!summaryData && !loadingSummary && <Empty description="Apply filters to view insights." />}
				{summaryData && (
					<Space direction="vertical" size="large" style={{ width: '100%' }}>
						<Row gutter={[16, 16]}>
							<Col xs={24} md={12} lg={8}>
								<Card>
									<Statistic title="Total Calls" value={summaryData.overallStats.totalCalls} />
								</Card>
							</Col>
							<Col xs={24} md={12} lg={8}>
								<Card>
									<Statistic
										title="Avg. Call Score"
										value={summaryData.overallStats.avgCallScore}
										precision={2}
										suffix="/ 5"
									/>
								</Card>
							</Col>
						</Row>

						<Card title="Interruptions Trend (Daily Total)">
							<Line {...lineChartConfig} data={summaryData.timeSeries.interruptions} />
						</Card>

						<Card title="Detailed Evaluation Metrics (Average Scores)">
							<Row gutter={[16, 16]}>
								{EVALUATION_FIELDS.map((key) => (
									<Col span={12} key={key}>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												marginBottom: '4px',
											}}
										>
											<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
												<Text strong>{formatMetricName(key)}</Text>
												<Tooltip title={METRIC_DESCRIPTIONS[key]} placement="topLeft">
													<QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
												</Tooltip>
											</div>
											<Text type="secondary">{`${(summaryData.detailedEvaluationAvgs[key] || 0).toFixed(2)} / 10`}</Text>
										</div>
										<Progress
											percent={((summaryData.detailedEvaluationAvgs[key] || 0) / 10) * 100}
											showInfo={false}
											strokeColor={{ '0%': '#D32F2F', '70%': '#FBC02D', '100%': '#388E3C' }}
										/>
									</Col>
								))}
							</Row>
						</Card>

						<Card title="Individual Metric Trend Analysis">
							<Select
								value={trendMetric}
								onChange={setTrendMetric}
								style={{ width: 240, marginBottom: 16 }}
							>
								{EVALUATION_FIELDS.map((field) => (
									<Select.Option key={field} value={field}>
										{formatMetricName(field)}
									</Select.Option>
								))}
							</Select>
							<Line
								{...lineChartConfig}
								data={summaryData.timeSeries[trendMetric]}
								seriesField={undefined}
								yAxis={{ min: 0, max: 10 }}
							/>
						</Card>

						<Card title="Metric Impact Analysis">
							<Space wrap style={{ marginBottom: 16 }} align="center">
								<Text>Impact of:</Text>
								<Select
									value={impactXMetric}
									onChange={setImpactXMetric}
									style={{ width: 200 }}
									options={RAW_COUNT_METRICS.map((m) => ({ label: formatMetricName(m), value: m }))}
								/>
								<Text>on:</Text>
								<Text strong>{formatMetricName(impactYMetric)}</Text>
							</Space>
							<Column
								data={impactChartData}
								xField="bin"
								yField="avgValue"
								height={300}
								yAxis={{
									title: { text: `Avg. ${formatMetricName(impactYMetric)}` },
									min: 0,
								}}
								xAxis={{
									title: { text: formatMetricName(impactXMetric) },
								}}
								tooltip={{
									formatter: (datum) => {
										return {
											name: `Avg. ${formatMetricName(impactYMetric)}`,
											value: `${datum.avgValue.toFixed(2)} (from ${datum.callVolume} calls)`,
										};
									},
								}}
							/>
						</Card>

						<Card title="Detailed Call Logs">
							{tableData.length > 0 || loadingTable ? (
								<Spin spinning={loadingTable}>
									<Table
										dataSource={tableData}
										columns={tableColumns}
										rowKey="callSid"
										scroll={{ x: 800 }}
										onRow={(record) => ({
											onClick: () => onViewDetail(record.callSid),
											style: { cursor: 'pointer' },
										})}
										pagination={{
											current: currentPage,
											pageSize: DEFAULT_PAGE_LIMIT,
											total: totalItems,
											onChange: (page) => {
												const filters = form.getFieldsValue();
												fetchTableData(filters, page);
											},
											showSizeChanger: false,
										}}
									/>
								</Spin>
							) : (
								<Empty description="Select a Call Score and click 'Apply' to view call logs." />
							)}
						</Card>
						<ShareCallLogModal />
					</Space>
				)}
			</Spin>
		</Space>
	);
};

export default CallInsightsDashboard;
